import React, { useState, useEffect, useCallback } from "react";
import { useParams } from "react-router-dom";
import { useEditorStore } from "@/stores/editor/editor-store";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { ScrollArea } from "@/components/ui/scroll-area";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { Database, ChevronDown, Loader2, FileText } from "lucide-react";
import { queryTemplateApi } from "@/services/api/queryTemplateApi";
import type { QueryTemplate } from "@/hooks/data";
import { cn } from "@/lib/utils";
import {
  FOREST_GREEN,
  CHARCOAL,
  BORDER,
  MID_GRAY,
  LIGHT_GRAY,
  WHITE,
  SHADOWS,
} from "@/styles/tokens";

export const DataTemplateSelector: React.FC<{ className?: string }> = ({ className }) => {
  const { projectId } = useParams<{ projectId: string }>();
  const dataSourceId = useEditorStore((state) => state.dataSourceId);
  const enterDataMode = useEditorStore((state) => state.enterDataMode);

  const [templates, setTemplates] = useState<QueryTemplate[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const [hasLoaded, setHasLoaded] = useState(false);

  const loadTemplates = useCallback(async () => {
    try {
      setIsLoading(true);
      const result = await queryTemplateApi.list({ limit: 50 });
      setTemplates(result.templates);
      setHasLoaded(true);
    } catch {
      // Silently fail - dropdown will show empty state
    } finally {
      setIsLoading(false);
    }
  }, []);

  // Load on first open
  useEffect(() => {
    if (isOpen && !hasLoaded) {
      loadTemplates();
    }
  }, [isOpen, hasLoaded, loadTemplates]);

  // Also load on mount so we can show the current template name
  useEffect(() => {
    if (!hasLoaded) loadTemplates();
  }, [hasLoaded, loadTemplates]);

  const currentTemplate = templates.find((t) => (t.id || t._id) === dataSourceId);
  const displayName = currentTemplate?.name || "Select Template";

  const handleSelect = (template: QueryTemplate) => {
    const id = template.id || template._id || '';
    if (projectId && id) {
      try { localStorage.setItem(`lastDataTemplate:${projectId}`, id); } catch {}
    }
    enterDataMode(id, template.name);
    setIsOpen(false);
  };

  return (
    <TooltipProvider>
      <DropdownMenu open={isOpen} onOpenChange={setIsOpen}>
        <Tooltip>
          <TooltipTrigger asChild>
            <DropdownMenuTrigger asChild>
              <Button
                variant="outline"
                size="sm"
                className={cn(
                  "h-8 min-w-[180px] max-w-[220px] justify-between px-2.5",
                  "transition-all duration-150 ease-out",
                  "hover:bg-gray-50 active:scale-[0.98]",
                  className
                )}
                style={{
                  borderColor: BORDER.input,
                  backgroundColor: WHITE,
                }}
                aria-label={`Current template: ${displayName}. Click to switch templates.`}
                aria-haspopup="menu"
              >
                <div className="flex items-center gap-1.5 min-w-0">
                  <Database
                    className="w-4 h-4 flex-shrink-0"
                    style={{ color: FOREST_GREEN.DEFAULT }}
                  />
                  <span
                    className="text-sm font-medium truncate"
                    style={{ color: CHARCOAL.DEFAULT, maxWidth: "120px" }}
                  >
                    {displayName}
                  </span>
                </div>
                <div className="flex items-center gap-1.5 flex-shrink-0">
                  {currentTemplate && (
                    <Badge
                      variant="secondary"
                      style={{
                        fontSize: "10px",
                        backgroundColor: LIGHT_GRAY.dark,
                        color: CHARCOAL.DEFAULT,
                        padding: "1px 6px",
                      }}
                    >
                      {currentTemplate.usageCount ?? 0} uses
                    </Badge>
                  )}
                  <ChevronDown
                    className="w-3.5 h-3.5"
                    style={{ color: MID_GRAY.DEFAULT }}
                  />
                </div>
              </Button>
            </DropdownMenuTrigger>
          </TooltipTrigger>
          <TooltipContent side="bottom">
            <p>Switch data templates</p>
          </TooltipContent>
        </Tooltip>

        <DropdownMenuContent
          align="end"
          className="w-[280px] p-0"
          style={{
            backgroundColor: WHITE,
            border: `1px solid ${BORDER.DEFAULT}`,
            borderRadius: "6px",
            boxShadow: SHADOWS.cardHover,
          }}
        >
          <ScrollArea className="max-h-[400px]">
            <div className="py-1">
              {isLoading ? (
                <div className="flex items-center justify-center py-6 text-sm text-muted-foreground">
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Loading...
                </div>
              ) : templates.length === 0 ? (
                <div className="px-3 py-6 text-center text-sm text-muted-foreground">
                  No templates found
                </div>
              ) : (
                templates.map((template) => {
                  const id = template.id || template._id;
                  const isActive = id === dataSourceId;
                  return (
                    <button
                      key={id}
                      onClick={() => handleSelect(template)}
                      className={cn(
                        "w-full flex items-center gap-2 px-3 py-2 text-left",
                        "transition-colors duration-150",
                        "hover:bg-gray-50",
                        isActive && "bg-primary/5"
                      )}
                      style={{
                        borderLeft: isActive
                          ? `3px solid ${FOREST_GREEN.DEFAULT}`
                          : "3px solid transparent",
                      }}
                    >
                      <FileText className="w-4 h-4 flex-shrink-0" style={{ color: MID_GRAY.DEFAULT }} />
                      <div className="flex-1 min-w-0">
                        <span
                          className="text-sm truncate block"
                          style={{ color: isActive ? FOREST_GREEN.DEFAULT : CHARCOAL.DEFAULT }}
                        >
                          {template.name}
                        </span>
                        {template.description && (
                          <span className="text-xs text-muted-foreground truncate block">
                            {template.description}
                          </span>
                        )}
                      </div>
                      <Badge
                        variant="secondary"
                        style={{
                          fontSize: "10px",
                          backgroundColor: LIGHT_GRAY.dark,
                          color: CHARCOAL.DEFAULT,
                          padding: "1px 6px",
                        }}
                      >
                        {template.usageCount ?? 0}
                      </Badge>
                    </button>
                  );
                })
              )}
            </div>
          </ScrollArea>
        </DropdownMenuContent>
      </DropdownMenu>
    </TooltipProvider>
  );
};
